import { useMutation } from "@tanstack/react-query"
import { History, Link2, AlertTriangle, Sparkles, RefreshCw } from "lucide-react"
import { VORGANG_OHNE_NAME, vorgangBezeichnung } from "@/lib/vorgangName"

/**
 * Verknüpfungs-Vorschläge für eine noch nicht verknüpfte Mail (HERO-Tenants).
 *
 * Zwei Quellen, bewusst unterschiedlich gewichtet:
 *  1. **Historie** — mit wem wurde derselbe Absender früher verknüpft? Die
 *     Entscheidung hat damals ein Mensch getroffen, deshalb steht sie oben.
 *  2. **HERO-Treffer** — Kontakte mit dieser E-Mail-Adresse. Nur Vorschlag:
 *     mehrere Kontakte können sich eine Adresse teilen (belegt bei sanitec,
 *     31.07.2026: e-m@example.org gehört zu drei Kontakten).
 *
 * Nichts davon wird automatisch gesetzt. Ein falsch zugeordneter Kontakt fällt
 * erst auf, wenn eine Rechnung beim Falschen landet — der Klick bleibt beim Menschen.
 */
export type LinkSuggestion = {
  contact_id: number
  contact_name: string
  email: string
  address: string
  project_id?: number | null
  anzahl: number
  zuletzt?: string | null
}

/** Regelmäßig wiederkehrende Termine an einem Vorgang (#57). */
export type TurnusInfo = {
  regelmaessig: boolean
  turnus: string
  abstand_tage: number
  termine_gesamt: number
  termine_kuenftig: number
  naechster_termin?: string | null
  letzter_termin?: string | null
}

export type PlanVorgangVorschlag = {
  project_id: number
  project_nr?: string
  title?: string
  status_name?: string
  bezeichnung?: string
  turnus?: TurnusInfo | null
}

export type VorgangKandidat = {
  project_id: number
  project_nr?: string
  title?: string
  status_name?: string
  street?: string
  city?: string
  customer_name?: string
  grund?: string
  turnus?: TurnusInfo | null
}

export type LinkSuggestions = {
  sender_email?: string
  historie: LinkSuggestion[]
  hero_treffer: LinkSuggestion[]
  mehrdeutig: boolean
  vorgang_laut_plan?: PlanVorgangVorschlag | null
  vorgang_kandidaten?: VorgangKandidat[]
  vorgang_eindeutig?: boolean
  hinweis: string
}

const datumKurz = (iso?: string | null) => {
  if (!iso) return ""
  const [y, m, d] = iso.split("-")
  return y && m && d ? `${d}.${m}.${y}` : iso
}

function VorschlagZeile({
  s, onUebernehmen, laeuft, betont,
}: {
  s: LinkSuggestion
  onUebernehmen: (s: LinkSuggestion) => void
  laeuft: boolean
  betont?: boolean
}) {
  // Ohne Namen bleibt die Nummer — dann aber nur EINMAL. Vorher stand da
  // "Kontakt #7007229 · #7007229", weil der Fallback und der Zusatz dieselbe
  // Zahl zeigten (Ticket #14).
  const name = s.contact_name || `Kontakt #${s.contact_id}`
  const zeigeNummer = !!s.contact_name
  return (
    <div style={{
      display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
      padding: "6px 8px", borderRadius: 6,
      background: betont ? "var(--secondary-bg)" : "transparent",
    }}>
      <span style={{ fontSize: 12.5 }}>
        <strong>{name}</strong>
        {zeigeNummer && <span style={{ color: "var(--text-muted)" }}> · #{s.contact_id}</span>}
      </span>
      {s.anzahl > 0 && (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {s.anzahl === 1 ? "1 Mail" : `${s.anzahl} Mails`}
          {s.zuletzt ? `, zuletzt ${datumKurz(s.zuletzt)}` : ""}
        </span>
      )}
      {s.address && (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>· {s.address}</span>
      )}
      <button
        className="ev2-hero-ibtn ev2-hero-ibtn-primary"
        style={{ marginLeft: "auto" }}
        onClick={() => onUebernehmen(s)}
        disabled={laeuft}
        title={`Mail mit ${name} verknüpfen`}
      >
        <Link2 className="w-3.5 h-3.5" /> {laeuft ? "…" : "Übernehmen"}
      </button>
    </div>
  )
}

/** Beschriftung eines Kandidaten — Nummer und Titel, nie die interne ID. */
function kandidatLabel(k: VorgangKandidat): string {
  // #98: neutral statt „Vorgang #7284194" — Objekt und Kunde stehen darunter.
  return vorgangBezeichnung(k.project_nr, k.title) || VORGANG_OHNE_NAME
}

/**
 * „Vorgang laut Plan" — die Vorgänge, die zu dieser Mail passen könnten.
 *
 * 🔴 Bis zum 17.08.2026 stand hier genau EIN Vorschlag, und ohne ihn wurde die
 * Zeile ausgeblendet (`if (!vorschlag?.project_id) return null`). „Nicht
 * eindeutig" sah damit aus wie „nichts gefunden" — das Büro erfuhr nicht
 * einmal, dass es Kandidaten gab.
 *
 * Anlass war Mail #5941: zwei offene Vorgänge „Wartung whg Beispielhof" und „WHG:
 * Beispielhof" an derselben Straße, an verschiedenen Kunden. Aus einer Mail ohne
 * Objektangabe ist das nicht auflösbar — von niemandem. Also wird gewählt,
 * nicht geraten. Deshalb stehen Objektadresse und Kunde in jeder Zeile: daran
 * hält man zwei gleichnamige Vorgänge auseinander.
 *
 * Gegenprobe an den 45 zugeordneten Sanitec-Mails: 33 Treffer, 0 Fehlgriffe.
 * Trotzdem kein Automatismus — eine falsche Zuordnung fällt erst auf, wenn
 * eine Rechnung am falschen Vorgang hängt.
 */
export function PlanVorgangZeile({
  emailId, vorschlag, kandidaten, eindeutig, onApplied,
}: {
  emailId: number
  vorschlag?: PlanVorgangVorschlag | null
  kandidaten?: VorgangKandidat[]
  eindeutig?: boolean
  onApplied: () => void
}) {
  const uebernehmen = useMutation({
    mutationFn: async (projectId: number) => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: projectId }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: onApplied,
  })

  // Ohne Kandidaten aus dem Backend auf den Einzelvorschlag zurückfallen —
  // ein älterer Server liefert das neue Feld nicht.
  const liste: VorgangKandidat[] = (kandidaten && kandidaten.length)
    ? kandidaten
    : (vorschlag?.project_id
      ? [{
        project_id: vorschlag.project_id,
        project_nr: vorschlag.project_nr,
        title: vorschlag.title || vorschlag.bezeichnung,
        status_name: vorschlag.status_name,
        grund: "vom Planer genannt",
        turnus: vorschlag.turnus,   // #57
      }]
      : [])

/**
 * „Läuft hier eine regelmäßige Reinigung?" (#57, Marlene 27.08.2026)
 *
 * Die Antwort entscheidet, wie geantwortet wird: Bestandsobjekt im Turnus oder
 * Einzelanfrage. Sie steht nirgends gepflegt — der Kalender weiß es (am
 * gemeldeten Objekt 44 Termine im 14-Tage-Takt), deshalb wird sie abgeleitet.
 *
 * Ohne erkennbares Muster wird NICHTS gezeigt: „unregelmäßig" wäre eine
 * Behauptung, für die die Daten nicht reichen.
 */
function TurnusBadge({ turnus }: { turnus?: TurnusInfo | null }) {
  if (!turnus?.regelmaessig) return null
  const naechster = turnus.naechster_termin
    ? new Date(turnus.naechster_termin).toLocaleDateString("de-DE",
        { day: "2-digit", month: "2-digit", year: "numeric" })
    : null
  return (
    <div
      style={{
        fontSize: 12, marginTop: 4, display: "flex", alignItems: "center",
        gap: 5, color: "#047857",
      }}
      title={`${turnus.termine_gesamt} Termine im Kalender, davon ${turnus.termine_kuenftig} künftig`}
    >
      <RefreshCw className="w-3 h-3" style={{ flexShrink: 0 }} />
      <span>
        <strong>Regelmäßig ({turnus.turnus})</strong>
        {naechster && <> — nächster Termin {naechster}</>}
      </span>
    </div>
  )
}

  if (!liste.length) return null
  const mehrere = liste.length > 1 || eindeutig === false

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{
        display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 6,
      }}>
        {mehrere
          ? <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#b45309", flexShrink: 0, marginTop: 1 }} />
          : <Sparkles className="w-3.5 h-3.5" style={{ color: "#0369a1", flexShrink: 0, marginTop: 1 }} />}
        <span style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4 }}>
          {mehrere
            ? <><strong>Vorgang nicht eindeutig</strong> — {liste.length} kommen in Frage:</>
            : "Vorgang laut Plan:"}
        </span>
      </div>

      {/* Jeder Kandidat als eigene Karte. In der schmalen Seitenleiste bricht
          sonst alles ineinander und man sieht nicht, wo der eine aufhoert und
          der naechste anfaengt — die Auswahl ist aber genau die Aufgabe. */}
      <div style={{ display: "grid", gap: 6 }}>
        {liste.map((k) => {
          const label = kandidatLabel(k)
          const objekt = [k.street, k.city].filter(Boolean).join(", ")
          return (
            <div
              key={k.project_id}
              style={{
                border: "1px solid var(--border)", borderRadius: 6,
                padding: "7px 9px", background: "var(--bg-card, transparent)",
              }}
            >
              <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>
                {label}
              </div>

              {/* Objekt und Kunde sind das Unterscheidungsmerkmal — ohne sie
                  ist die Auswahl nicht zu treffen. Je eigene Zeile, damit
                  nichts mittendrin umbricht. */}
              {objekt && (
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 3 }}>
                  📍 {objekt}
                </div>
              )}
              {k.customer_name && (
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  Kunde: {k.customer_name}
                </div>
              )}

              {/* #57: bei mehreren Kandidaten ist genau das der Unterschied */}
              <TurnusBadge turnus={k.turnus} />

              <div style={{
                display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                marginTop: 6,
              }}>
                {k.status_name && (
                  <span style={{
                    fontSize: 12, color: "var(--text-muted)", padding: "1px 6px",
                    border: "1px solid var(--border)", borderRadius: 999, whiteSpace: "nowrap",
                  }}>
                    {k.status_name}
                  </span>
                )}
                <button
                  className="ev2-hero-ibtn ev2-hero-ibtn-primary"
                  style={{ marginLeft: "auto" }}
                  onClick={() => uebernehmen.mutate(k.project_id)}
                  disabled={uebernehmen.isPending}
                  title={`Mail mit ${label} verknüpfen`}
                >
                  <Link2 className="w-3.5 h-3.5" /> {uebernehmen.isPending ? "…" : "Übernehmen"}
                </button>
              </div>

              {k.grund && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  {k.grund}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {uebernehmen.isError && (
        <div style={{ fontSize: 12, color: "var(--pp-danger, #c0392b)", marginTop: 6 }}>
          Verknüpfen fehlgeschlagen — bitte über „Verknüpfen" manuell versuchen.
        </div>
      )}
    </div>
  )
}

export function LinkSuggestionsBox({
  emailId, data, onApplied,
}: {
  emailId: number
  data?: LinkSuggestions
  onApplied: () => void
}) {
  const uebernehmen = useMutation({
    mutationFn: async (s: LinkSuggestion) => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        // Projekt bewusst NICHT mitsetzen: ein früher genutztes Projekt kann
        // längst abgeschlossen sein. Kontakt genügt, das Projekt wählt der
        // Nutzer danach bewusst.
        body: JSON.stringify({ contact_id: s.contact_id, contact_name: s.contact_name || "" }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: onApplied,
  })

  const historie = data?.historie || []
  const treffer = (data?.hero_treffer || []).filter(
    (t) => !historie.some((h) => h.contact_id === t.contact_id),
  )
  if (!historie.length && !treffer.length) return null

  return (
    <div style={{
      marginTop: 8, border: "1px solid var(--border)", borderRadius: 8,
      padding: 8, display: "flex", flexDirection: "column", gap: 4,
    }}>
      {historie.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
            <History className="w-3.5 h-3.5" />
            {historie.length === 1
              ? "Dieser Absender war schon einmal verknüpft:"
              : "Dieser Absender war schon mit mehreren Kontakten verknüpft:"}
          </div>
          {historie.map((s) => (
            <VorschlagZeile key={`h-${s.contact_id}`} s={s} betont
                            onUebernehmen={(x) => uebernehmen.mutate(x)}
                            laeuft={uebernehmen.isPending} />
          ))}
        </>
      )}

      {treffer.length > 0 && (
        <>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 12,
            marginTop: historie.length ? 6 : 0,
            color: data?.mehrdeutig ? "var(--pp-warning, #b45309)" : "var(--text-secondary)",
          }}>
            {data?.mehrdeutig ? <AlertTriangle className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {data?.mehrdeutig
              ? `${treffer.length} HERO-Kontakte teilen sich diese Adresse — bitte den richtigen wählen:`
              : "In HERO mit dieser Adresse gefunden:"}
          </div>
          {treffer.map((s) => (
            <VorschlagZeile key={`t-${s.contact_id}`} s={s}
                            onUebernehmen={(x) => uebernehmen.mutate(x)}
                            laeuft={uebernehmen.isPending} />
          ))}
        </>
      )}

      {uebernehmen.isError && (
        <div style={{ fontSize: 12, color: "var(--pp-danger, #c0392b)" }}>
          Verknüpfen fehlgeschlagen — bitte über „Verknüpfen" manuell versuchen.
        </div>
      )}
    </div>
  )
}
